import { useState } from 'react'
import { NavLink } from 'react-router-dom'
import {
  ROTULO_STATUS_USUARIO,
  STATUS_USUARIO,
  type StatusUsuario,
  type UsuarioSessao,
} from '@crm/shared'
import { useAlterarPreferencias, useAlterarStatus, useLogout } from '../api/hooks'
import { tocarToque, type Toque } from '../tempo-real/som'
import { useFecharAoClicarFora } from './fechar-fora'
import {
  IconeConfiguracao,
  IconeLua,
  IconeSair,
  IconeSom,
  IconeTocar,
} from './icones'
import { abreConfiguracao, pode } from '../permissao'
import { juntar } from './ui'

/**
 * Nome, status e sair, atrás de um clique.
 *
 * Os três ficavam soltos na barra do topo e comiam a largura de dois itens de
 * menu para mostrar o tempo todo o que se olha uma vez por dia. Recolhidos,
 * sobra espaço para a navegação, que é o que se usa a toda hora.
 *
 * O status continua visível fora do menu, como uma bolinha colorida na
 * inicial: é o único dos três que muda sozinho e que a pessoa precisa
 * conferir de relance — "estou pausado sem querer?" é uma pergunta real.
 */

const COR_DO_STATUS: Record<StatusUsuario, string> = {
  online: 'bg-green-500',
  pausado: 'bg-amber-500',
  offline: 'bg-tinta-400',
}


/** As iniciais da bolinha de pessoa: "Mike" vira "MI", "Ana Souza" vira "AS". */
function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/)
  if (partes.length === 1) return (partes[0] as string).slice(0, 2).toUpperCase()
  return ((partes[0] as string)[0] + (partes[partes.length - 1] as string)[0]).toUpperCase()
}

export function MenuDoUsuario({ usuario }: { usuario: UsuarioSessao }) {
  const [aberto, setAberto] = useState(false)
  const alterarStatus = useAlterarStatus()
  const alterarPreferencias = useAlterarPreferencias()
  const logout = useLogout()
  const preferencias = usuario.preferencias
  const escuro = preferencias.tema === 'escuro'
  // O volume anda com o dedo; vai para a conta só quando solta.
  const [volume, setVolume] = useState<number | null>(null)
  const volumeNaTela = volume ?? preferencias.volumeSom

  function gravarVolume() {
    if (volume === null) return
    alterarPreferencias.mutate({ volumeSom: volume })
    tocarToque('minha', volume)
    setVolume(null)
  }
  const caixa = useFecharAoClicarFora<HTMLDivElement>(aberto, () => setAberto(false))

  return (
    <div ref={caixa} className="relative">
      <button
        type="button"
        onClick={() => setAberto((a) => !a)}
        aria-label={`${usuario.nome}, ${ROTULO_STATUS_USUARIO[usuario.status]}`}
        title={`${usuario.nome}, ${ROTULO_STATUS_USUARIO[usuario.status]}`}
        className="relative flex h-8 w-8 items-center justify-center rounded-full bg-marca text-xs font-medium text-white hover:bg-marca-escura"
      >
        {iniciais(usuario.nome)}
        <span
          className={juntar(
            'absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-superficie',
            COR_DO_STATUS[usuario.status],
          )}
        />
      </button>

      {aberto ? (
        <div className="absolute right-0 top-full z-40 mt-2 w-64 overflow-hidden rounded-2xl ring-1 ring-tinta-200 bg-superficie py-1.5 shadow-alta">
          <div className="border-b border-tinta-100 px-3 py-2">
            <p className="truncate text-sm font-medium text-tinta-900">{usuario.nome}</p>
            <p className="truncate text-xs text-tinta-400">{usuario.email}</p>
            {usuario.proprietario ? (
              <p className="mt-0.5 text-xs font-medium text-marca">Proprietário</p>
            ) : null}
          </div>

          <div className="border-b border-tinta-100 px-3 py-2">
            <span className="mb-1 block text-xs text-tinta-400">Meu status</span>
            <div className="space-y-0.5">
              {STATUS_USUARIO.map((status) => (
                <button
                  key={status}
                  type="button"
                  // O status que já está não vai de novo: cada clique gravava uma linha nos logs.
                  onClick={() => {
                    if (status !== usuario.status) alterarStatus.mutate(status)
                  }}
                  aria-pressed={usuario.status === status}
                  className={juntar(
                    'flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm',
                    usuario.status === status
                      ? 'bg-marca/10 font-medium text-marca'
                      : 'text-tinta-600 hover:bg-tinta-100',
                  )}
                >
                  <span className={juntar('h-2 w-2 rounded-full', COR_DO_STATUS[status])} />
                  {ROTULO_STATUS_USUARIO[status]}
                </button>
              ))}
            </div>
          </div>

          {/*
            Som e tema de cada pessoa (ADR 0040). Moram aqui porque são da
            pessoa, e não da empresa: ninguém liga o som de outra.
          */}
          <div className="border-b border-tinta-100 px-3 py-2">
            <span className="mb-1 flex items-center gap-1.5 text-xs text-tinta-400">
              <IconeSom className="h-3.5 w-3.5" />
              Som das mensagens
            </span>
            <LinhaDeSom
              rotulo="Minhas conversas"
              toque="minha"
              ligado={preferencias.somMinhas}
              volume={volumeNaTela}
              aoMudar={(ligado) => alterarPreferencias.mutate({ somMinhas: ligado })}
            />
            {pode(usuario, 'bate_papo.fila') ? (
              <LinhaDeSom
                rotulo="Fila"
                toque="fila"
                ligado={preferencias.somFila}
                volume={volumeNaTela}
                aoMudar={(ligado) => alterarPreferencias.mutate({ somFila: ligado })}
              />
            ) : null}
            <label className="mt-1.5 flex items-center gap-2 px-2 text-xs text-tinta-500">
              Volume
              <input
                type="range"
                min={0.05}
                max={1}
                step={0.05}
                value={volumeNaTela}
                onChange={(e) => setVolume(Number(e.target.value))}
                onPointerUp={gravarVolume}
                onKeyUp={gravarVolume}
                aria-label="Volume do som das mensagens"
                className="h-1 min-w-0 flex-1 accent-marca"
              />
            </label>
          </div>

          <button
            type="button"
            role="switch"
            aria-checked={escuro}
            onClick={() => alterarPreferencias.mutate({ tema: escuro ? 'claro' : 'escuro' })}
            className="flex w-full items-center justify-between gap-2 border-b border-tinta-100 px-3 py-2 text-left text-sm text-tinta-600 hover:bg-tinta-100"
          >
            <span className="flex items-center gap-2">
              <IconeLua className="h-4 w-4" />
              Modo escuro
            </span>
            <Interruptor ligado={escuro} />
          </button>

          {/*
            Configuração mora aqui, e não na navegação: é tela de ajustar e
            sair, não de trabalhar. A permissão é a mesma que a barra usava.
          */}
          {abreConfiguracao(usuario) ? (
            <NavLink
              to="/configuracao"
              onClick={() => setAberto(false)}
              className={({ isActive }) =>
                juntar(
                  'flex w-full items-center gap-2 px-3 py-2 text-left text-sm',
                  isActive ? 'font-medium text-marca' : 'text-tinta-600 hover:bg-tinta-100',
                )
              }
            >
              <IconeConfiguracao className="h-4 w-4" />
              Configuração
            </NavLink>
          ) : null}

          <button
            type="button"
            onClick={() => logout.mutate()}
            disabled={logout.isPending}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-tinta-600 hover:bg-tinta-100 disabled:opacity-50"
          >
            <IconeSair className="h-4 w-4" />
            Sair
          </button>
        </div>
      ) : null}
    </div>
  )
}

/** Uma linha de som: liga e desliga, e o botão de ouvir o toque daquele tipo. */
function LinhaDeSom({
  rotulo,
  toque,
  ligado,
  volume,
  aoMudar,
}: {
  rotulo: string
  toque: Toque
  ligado: boolean
  volume: number
  aoMudar: (ligado: boolean) => void
}) {
  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        role="switch"
        aria-checked={ligado}
        onClick={() => aoMudar(!ligado)}
        className="flex min-w-0 flex-1 items-center justify-between gap-2 rounded px-2 py-1 text-left text-sm text-tinta-600 hover:bg-tinta-100"
      >
        {rotulo}
        <Interruptor ligado={ligado} />
      </button>
      <button
        type="button"
        onClick={() => tocarToque(toque, volume)}
        title={`Ouvir o toque de ${rotulo.toLowerCase()}`}
        aria-label={`Ouvir o toque de ${rotulo.toLowerCase()}`}
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-tinta-400 hover:bg-tinta-100 hover:text-tinta-700"
      >
        <IconeTocar className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

/** O desenho do interruptor. Quem recebe o clique é o botão em volta. */
function Interruptor({ ligado }: { ligado: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={juntar(
        'relative inline-flex h-4 w-7 shrink-0 rounded-full transition-colors',
        ligado ? 'bg-marca' : 'bg-tinta-300',
      )}
    >
      <span
        className={juntar(
          'absolute top-0.5 h-3 w-3 rounded-full bg-white shadow-suave transition-transform',
          ligado ? 'translate-x-3.5' : 'translate-x-0.5',
        )}
      />
    </span>
  )
}
